import { NavLink, NavLinkProps } from 'react-router-dom'
import { RainbowKitCustomButton } from '../RainbowKitCustomButton'
import {
	Drawer,
	DrawerClose,
	DrawerContent,
	DrawerDescription,
	DrawerFooter,
	DrawerTitle,
	DrawerTrigger
} from '@/components/ui/Drawer'
import { cn, composeRenderProps } from '@/lib/utils'
import { Menu } from 'lucide-react'
import LogoutIcon from '@/assets/icons/Logout.svg?react'
import XIcon from '@/assets/icons/Close.svg?react'
import { useDisconnectConnectors } from '@/hooks/useDisconnectConnectors'
import { useAccount } from 'wagmi'
import { Avatar } from '../ui/Avatar'
import { useAccountModal } from '@rainbow-me/rainbowkit'
import { useAccountChainStatus } from '@/hooks/useAccountChainStatus'
import { useActiveLink } from '@/hooks/useActiveLink.ts'
import React, { useState } from 'react'
import External from '@/assets/icons/External.svg?react'
import { testIdFactory } from '@/utils/test-id-factory.ts'
import { SHOW_ANNOUNCEMENT_BAR } from '@/config/constants.ts'

const NavLinks = ({ isActive, onClick, ...props }: { isActive: boolean; onClick?: () => void } & NavLinkProps) => (
	<NavLink
		{...props}
		onClick={e => {
			onClick?.()
		}}
		className={composeRenderProps(props.className, prevClassName => {
			return cn(
				(isActive ? 'border-b-2 text-danger font-bold' : 'text-secondary') +
					' text-[12px] max-w-fit border-danger py-2',
				prevClassName
			)
		})}
	/>
)
const MobileMenu = ({ links }) => {
	const { isConnected, address } = useAccount()
	const { openAccountModal } = useAccountModal()
	const disconnect = useDisconnectConnectors()
	const status = useAccountChainStatus()
	const { isActiveLink } = useActiveLink(links)
	const [isOpen, setIsOpen] = useState(false)

	return (
		<Drawer direction="left" open={isOpen} onOpenChange={setIsOpen}>
			<div
				style={{ top: SHOW_ANNOUNCEMENT_BAR ? 'calc(var(--announcement-h, 2.5rem) + 8px)' : '8px' }}
				className="fixed w-full left-[50%] translate-x-[-50%] z-50"
			>
				<div className="flex px-6 mx-2 h-[61px] items-center justify-between bg-neutral rounded-full drop-shadow-lg">
					<DrawerTrigger onClick={() => setIsOpen(true)}>
						<Menu />
					</DrawerTrigger>
					<img
						src="/images/pepper-logo.webp"
						alt="logo"
						className="absolute left-1/2 -translate-x-1/2 top-3 w-[80px] aspect-square"
					/>
					{status === 'unsupported' ? (
						<DrawerClose asChild>
							<RainbowKitCustomButton
								className="basis-1/3 text-center"
								buttonClass="p-2.5 leading-none w-full"
							/>
						</DrawerClose>
					) : (
						<button onClick={openAccountModal} data-testid={testIdFactory.walletPopupButton()}>
							<Avatar address={address as string} size={32} />
						</button>
					)}
				</div>
			</div>
			<DrawerContent
				className="top-0 w-3/4 rounded-tl-none rounded-bl-none rounded-2xl"
				defaultCloseButton={false}
			>
				<DrawerDescription className="sr-only">Navigation menu, use the arrows to navigate.</DrawerDescription>
				<div className="h-full bg-white opacity-100 rounded-tl-none rounded-bl-none rounded-2xl p-6 flex flex-col">
					<div className="flex justify-between items-center mb-8">
						<DrawerTitle className="text-[1.75rem] uppercase">Menu</DrawerTitle>
						<DrawerClose asChild onClick={() => setIsOpen(false)}>
							<XIcon className="size-6 fill-neutral-100" />
						</DrawerClose>
					</div>
					{links.map(link =>
						link?.external ? (
							<div className="flex flex-row justify-between content-center" key={link.label}>
								<a
									target="_blank"
									href={link.link}
									className="w-full max-w-none text-[1.5rem] mb-6"
									data-testid={link.testId}
								>
									{link.label}
								</a>
								<External className="w-[2.5rem] md:w-[1.5rem] mb-6" />
							</div>
						) : (
							<NavLinks
								key={link.label}
								className="w-full max-w-none text-[1.5rem] mb-6"
								to={link.link}
								isActive={isActiveLink(link.link)}
								onClick={() => setIsOpen(false)}
								data-testid={link.testId}
							>
								{link.label}
							</NavLinks>
						)
					)}
					<DrawerFooter className="p-0">
						<DrawerClose asChild>
							<div className="flex items-center gap-2 w-full justify-between">
								<RainbowKitCustomButton
									className="flex-col flex-1 text-center"
									buttonClass="px-10 py-5 w-full"
								/>
								{isConnected && (
									<LogoutIcon
										className="fill-danger"
										onClick={disconnect}
										data-testid={testIdFactory.walletDisconnectIconMobile()}
									/>
								)}
							</div>
						</DrawerClose>
					</DrawerFooter>
				</div>
			</DrawerContent>
		</Drawer>
	)
}

export default MobileMenu
